import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CombosConfiguration, MessageService, params } from 'brainloper-ui';
import { HistoryInventory } from 'src/app/models/inventory/history-inventory';
import { InputOrOutputInventory } from 'src/app/models/inventory/input-or-output-inventory';
import { Warehouse } from 'src/app/models/inventory/warehouse';
import { Provider } from 'src/app/models/administration/provider';
import { PurchaseOrder } from 'src/app/models/inventory/purchase-order';
import { RentalOrder } from 'src/app/models/rental/rental-order';
import { User } from 'src/app/models/administration/user';
import { EndpointDictionary } from 'src/app/utils/endpoints-dictionary';
import { enumProviderType } from 'src/app/models/commos/enum-provider-type';
import { enumWorkOrderStates } from 'src/app/models/maintenance/work-order';

export interface HandlerItemsHeaderConfig {
  // Feature flags
  view: boolean;
  fromPackOff: boolean;
  addWarehouse: boolean;
  showProvider: boolean;
  addProvider: boolean;
  showPurchaseOrder: boolean;
  comboPurchaseOrder: boolean;
  comboWorkOrder: boolean;
  workOrderState: enumWorkOrderStates;
  addRentalOrder: boolean;
  showTotal: boolean;
  addAuthorize: boolean;
  addReceptionDate: boolean;
  showReplyButton: boolean;
  showPrintButton: boolean;
  showTargetWarehouse: boolean;
  // Display data
  itemsData: InputOrOutputInventory[];
  labels: string[];
  isMobile: boolean;
  receptionDate: string | Date;
  editHistoryInventory: HistoryInventory;
  // Reactive state (changes trigger combo reloads)
  warehouseId: number;
  destinationWarehouseId: number;
  providerId: number;
  userAuthorizeId: number;
  rentalOrderId: number;
  workOrderId: number;
  paramsWarehouse: params[];
  paramsTargetWarehouse: params[];
  paramsRentalOrder: params[];
}

@Component({
  selector: 'app-handler-items-header',
  templateUrl: './handler-items-header.component.html',
  styleUrls: ['./handler-items-header.component.scss'],
  standalone: false
})
export class HandlerItemsHeaderComponent implements OnChanges {
  @Input() config: HandlerItemsHeaderConfig;

  constructor(private message: MessageService) {}

  @Output() onOpenDialogFunction = new EventEmitter<void>();
  @Output() onReplyRouteNavigate = new EventEmitter<void>();
  @Output() onPrint = new EventEmitter<void>();
  @Output() onValueWarehouse = new EventEmitter<Warehouse>();
  @Output() onDataComboWarehouse = new EventEmitter<Warehouse[]>();
  @Output() onValueTargetWarehouse = new EventEmitter<Warehouse>();
  @Output() onDataComboTargetWarehouse = new EventEmitter<Warehouse[]>();
  @Output() onValueProvider = new EventEmitter<number>();
  @Output() onDataComboProvider = new EventEmitter<Provider[]>();
  @Output() onChangesComboPurchaseOrder = new EventEmitter<number>();
  @Output() onDataComboPurchaseOrder = new EventEmitter<PurchaseOrder[]>();
  @Output() onChangesComboWorkOrder = new EventEmitter<number>();
  @Output() onChangesComboRentalOrder = new EventEmitter<RentalOrder>();
  @Output() onDataComboRentalOrder = new EventEmitter<RentalOrder[]>();
  @Output() onValueUser = new EventEmitter<number>();
  @Output() onDataComboUser = new EventEmitter<User[]>();
  @Output() receptionDateChange = new EventEmitter<string | Date>();

  public hasOneWarehouse = false;
  public hasOneDestinationWarehouse = false;
  public readonly firstInRow = [{ id: 0, consecutive: 'No Asociar Orden' }];

  // Previous values to detect meaningful changes within the config object
  private _prevWarehouseId: number;
  private _prevDestinationWarehouseId: number;
  private _prevProviderId: number;
  private _prevEditHistoryInventory: HistoryInventory;
  private _prevUserAuthorizeId: number;
  private _prevWorkOrderState: enumWorkOrderStates;
  private _prevParamsWarehouse: params[];
  private _prevParamsTargetWarehouse: params[];
  private _prevParamsRentalOrder: params[];

  private paramsProvider: params[] = [{ id: 'providerTypeId', value: enumProviderType.ALMACEN }];
  private paramsPurchaseOrderByState: params[] = [
    { id: 'providerId', value: 0 },
    { id: 'stateId', value: 2 },
    { id: 'warehouseId', value: 0 },
  ];
  private paramsWorkOrderByState: params[] = [
    { id: 'active', value: 1 },
    { id: 'authorized', value: 1 },
    { id: 'readyResources', value: 1 },
  ];

  public reloadComboWarehouse = new EventEmitter<any>();
  public comboConfigWarehouse: CombosConfiguration = {
    visibleField: 'name',
    selectionField: 'id',
    urlCombo: EndpointDictionary.Warehouse.WAREHOUSE_COMBO,
    urlParams: [],
    label: 'Bodega',
  };

  public reloadComboTargetWarehouse = new EventEmitter<any>();
  public comboConfigTargetWarehouse: CombosConfiguration = {
    visibleField: 'name',
    selectionField: 'id',
    urlCombo: EndpointDictionary.Warehouse.WAREHOUSE_COMBO,
    urlParams: [],
    label: 'Bodega Destino',
  };

  public reloadComboProvider = new EventEmitter<any>();
  public comboConfigProvider: CombosConfiguration = {
    visibleField: 'providerName',
    selectionField: 'id',
    urlCombo: EndpointDictionary.Provider.PROVIDER_TYPE_COMBO,
    urlParams: this.paramsProvider,
    label: 'Proveedor',
  };

  public reloadComboPurchaseOrderByState = new EventEmitter<any>();
  public comboConfigPurchaseOrderByState: CombosConfiguration = {
    visibleField: 'consecutive',
    selectionField: 'id',
    urlCombo: EndpointDictionary.PurchaseOrder.PURCHASE_ORDER_COMBO_STATE,
    urlParams: this.paramsPurchaseOrderByState,
    label: 'Orden de Compra',
  };

  public reloadComboWorkOrderByState = new EventEmitter<any>();
  public comboConfigWorkOrderByState: CombosConfiguration = {
    visibleField: 'consecutive',
    selectionField: 'id',
    urlCombo: EndpointDictionary.WorkOrder.WORK_ORDER_COMBO_STATE,
    urlParams: this.paramsWorkOrderByState,
    label: 'Orden de Trabajo',
  };

  public reloadComboRentalOrder = new EventEmitter<any>();
  public comboConfigRentalOrder: CombosConfiguration = {
    visibleField: 'consecutive',
    selectionField: 'id',
    urlCombo: EndpointDictionary.RentalOrder.RENTAL_ORDER_COMBO_STATE,
    urlParams: [],
    label: 'Orden de Alquiler',
  };

  public comboConfigUserAutorize: CombosConfiguration = {
    visibleField: 'name',
    selectionField: 'userId',
    urlCombo: EndpointDictionary.User.USER_COMBO,
    urlParams: [],
    label: 'Autoriza',
  };

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['config']) return;
    const c = this.config;

    if (c?.paramsWarehouse !== this._prevParamsWarehouse) {
      this._prevParamsWarehouse = c.paramsWarehouse;
      this.comboConfigWarehouse.urlParams = c.paramsWarehouse;
    }

    if (c?.paramsTargetWarehouse !== this._prevParamsTargetWarehouse) {
      this._prevParamsTargetWarehouse = c.paramsTargetWarehouse;
      this.comboConfigTargetWarehouse.urlParams = c.paramsTargetWarehouse;
    }

    if (c?.paramsRentalOrder !== this._prevParamsRentalOrder) {
      this._prevParamsRentalOrder = c.paramsRentalOrder;
      this.comboConfigRentalOrder.urlParams = c.paramsRentalOrder;
    }

    if (c?.warehouseId > 0 && c.warehouseId !== this._prevWarehouseId) {
      this._prevWarehouseId = c.warehouseId;
      this.comboConfigWarehouse.selected = c.warehouseId;
      this.reloadComboWarehouse.emit('selected');
      this.paramsPurchaseOrderByState[2].value = c.warehouseId;
      this.comboConfigProvider.selected = c.editHistoryInventory?.providerId ?? 0;
      this.reloadComboProvider.emit('selected');
      this.comboConfigPurchaseOrderByState.selected = -1;
      this.reloadComboPurchaseOrderByState.emit('selected');
    }

    if (c?.destinationWarehouseId > 0 && c.destinationWarehouseId !== this._prevDestinationWarehouseId) {
      this._prevDestinationWarehouseId = c.destinationWarehouseId;
      this.comboConfigTargetWarehouse.selected = c.destinationWarehouseId;
      this.reloadComboTargetWarehouse.emit('selected');
    }

    if (c?.providerId > 0 && c.providerId !== this._prevProviderId) {
      this._prevProviderId = c.providerId;
      this.paramsPurchaseOrderByState[0].value = c.providerId;
      this.paramsPurchaseOrderByState[2].value = c.warehouseId;
      this.reloadComboPurchaseOrderByState.emit();
    }

    if (c?.editHistoryInventory && c.editHistoryInventory !== this._prevEditHistoryInventory) {
      this._prevEditHistoryInventory = c.editHistoryInventory;
      if (c.addAuthorize) {
        this.comboConfigUserAutorize.selected = c.editHistoryInventory.authorize;
      }
      if (c.addProvider) {
        this.comboConfigProvider.selected = c.editHistoryInventory.providerId;
      }
    }

    if (c?.userAuthorizeId && c.userAuthorizeId !== this._prevUserAuthorizeId) {
      this._prevUserAuthorizeId = c.userAuthorizeId;
      this.comboConfigUserAutorize.selected = c.userAuthorizeId;
    }

    if (c?.workOrderState !== this._prevWorkOrderState) {
      this._prevWorkOrderState = c.workOrderState;
      if (c.workOrderState === enumWorkOrderStates.TO_DISPATCH && c.workOrderId) {
        this.paramsWorkOrderByState[2].value = 0;
        this.comboConfigWorkOrderByState.selected = c.workOrderId;
        this.reloadComboWorkOrderByState.emit();
      }
    }
  }

  dataComboWarehouse(warehouses: Warehouse[]) {
    if (warehouses.length === 1) {
      this.hasOneWarehouse = true;
      this.comboConfigWarehouse.selected = warehouses[0].id;
      this.reloadComboWarehouse.emit('selected');
      this.onValueWarehouse.emit(warehouses[0]);
    }
    this.onDataComboWarehouse.emit(warehouses);
  }

  dataComboTargetWarehouse(warehouses: Warehouse[]) {
    if (warehouses.length === 1) {
      this.hasOneDestinationWarehouse = true;
      this.comboConfigTargetWarehouse.selected = warehouses[0].id;
      this.reloadComboTargetWarehouse.emit('selected');
      this.onValueTargetWarehouse.emit(warehouses[0]);
    }
    this.onDataComboTargetWarehouse.emit(warehouses);
  }

  dataComboRentalOrder(orders: RentalOrder[]) {
    if (this.config?.rentalOrderId) {
      const rentalOrder = orders.find(x => x.id === this.config.rentalOrderId);
      if (rentalOrder) {
        this.comboConfigRentalOrder.selected = this.config.rentalOrderId;
        this.reloadComboRentalOrder.emit('selected');
        this.onChangesComboRentalOrder.emit(rentalOrder);
      }
    }
    this.onDataComboRentalOrder.emit(orders);
  }

  openDialogFunction() { this.onOpenDialogFunction.emit(); }
  replyRouteNavigate() { this.onReplyRouteNavigate.emit(); }
  print() { this.onPrint.emit(); }
  valueWarehouse(event: Warehouse) { this.onValueWarehouse.emit(event); }
  valueTargetWarehouse(event: Warehouse) {
    if (event.id === this.comboConfigWarehouse.selected) {
      this.message.Warning('Advertencia', 'La bodega destino debe ser diferente a la bodega origen');
      this.comboConfigTargetWarehouse.selected = null;
      this.reloadComboTargetWarehouse.emit('selected');
      this.hasOneDestinationWarehouse = false;
      return;
    }
    this.onValueTargetWarehouse.emit(event);
  }
  valueProvider(event: number) { this.onValueProvider.emit(event); }
  dataComboProvider(event: Provider[]) { this.onDataComboProvider.emit(event); }
  changesComboPurchaseOrder(event: number) { this.onChangesComboPurchaseOrder.emit(event); }
  dataComboPurchaseOrder(event: PurchaseOrder[]) { this.onDataComboPurchaseOrder.emit(event); }
  changesComboWorkOrder(event: number) { this.onChangesComboWorkOrder.emit(event); }
  changesComboRentalOrder(event: RentalOrder) { this.onChangesComboRentalOrder.emit(event); }
  valueUser(event: number) { this.onValueUser.emit(event); }
  dataComboUser(event: User[]) { this.onDataComboUser.emit(event); }
}
